import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { sendTicket } from "../../../store/modules/register/actions";
import { FILDS_ZENDEKS } from "../constants/recover.const";

import { Button, Input, Select, TMPopover, TMPopoverBody, TMPopoverHeader } from "../../../shared/components";
import { FormSupport } from "../styles";

import { isCPFValid } from "../../../utils/validator";
import useDevice from "../../../hook/useDevice";
import config from "../../../config/ConfigApplication";

const Support = () => {
	const [selectedField, setSelectedField] = useState("");
	const [statePopover, setStatePopover] = useState(undefined);
	const [isDataEmpty, setIsDataEmpty] = useState(true);

	const {
		register,
		handleSubmit,
		setValue,
		setError,
		clearErrors,
		watch,
		reset,
		resetField,
		formState,
		control,
	} = useForm({
		defaultValues: {
			alternative: "E-mail",
		},
	});

	const { alternative } = watch();

	const { ticketSent, ticketLoading } = useSelector((state) => state.register);

	const dispatch = useDispatch();
	const { isDesktop } = useDevice();

	useEffect(() => {
		if (ticketSent) {
			setStatePopover(true);
		}
	}, [ticketSent]);

	useEffect(() => {
		setSelectedField(alternative || "Telefone/E-mail");

		["oldData", "newData"].forEach((item) => resetField(item));
	}, [alternative]);

	useEffect(() => {
		const { unsubscribe } = watch((value, { name }) => {
			setIsDataEmpty(
				Object.values(value).some(
					(item) =>
						item == null ||
						(typeof item === "string" &&
							item.replace(/[^A-Za-z0-9]/, "").trim() === "")
				)
			);

			Object.entries(value).forEach(([field, value]) => {
				if (value) {
					handleValidField(value, field);
				}
			});
		});
		return () => unsubscribe();
	}, []);

	function submit(formData) {
		const { alternative, document, dtBirthday, newData, oldData, user, userContact } = formData;

		const payload = {
			ticket: {
				subject: "Solicitação de alteração cadastral - Recuperação de Senha",
				comment: {
					body: `Olá! Estou tentando resgatar a minha senha, mas mudei meus dados cadastrais.\r\nEstou tentando alterar o meu ${alternative}.\r\n Meus dados:\n\t Nome: ${user}\n\t CPF: ${document}\n\t Email/Telefone antigo: ${oldData}\n\t Email/Telefone novo: ${newData}\n\t Data de nascimento: ${dtBirthday}`,
				},
				via: {
					channel: "web",
				},
				status: "open",
				requester: {
					name: user,
					email: alternative === "Telefone" ? userContact : newData,
				},
				tags: ["chatbot", "chatbot_on", "problema"],
				custom_fields: [
					{
						id: FILDS_ZENDEKS.NAME,
						value: user,
					},
					{
						id: FILDS_ZENDEKS.DOCUMENT,
						value: document,
					},
					{
						id: FILDS_ZENDEKS.REASON,
						value: "Problema",
					},
					{
						id: FILDS_ZENDEKS.PROBLEM,
						value: "Cadastro",
					},
					{
						id: FILDS_ZENDEKS.ENROLL,
						value: "Recuperação de conta",
					},
					{
						id: FILDS_ZENDEKS.ENROLL,
						value: "recuperacao_de_conta",
					},
				],
				priority: "urgent",
			},
		};

		if (!isDataEmpty) {
			dispatch(sendTicket({ ticket: payload }));
		} else {
			toast.error("Preencha todos os campos para abrir o chamado");
		}
	}

	function handleChange(isOpen) {
		if (!isOpen) {
			clearErrors();
			reset();
			setIsDataEmpty(true);
		}
	}

	function handleValidField(value, field) {
		const action = {
			document: () => {
				const document = String(value.replace(/\.|-|_/g, ""));

				const isValid = isCPFValid(document);
				if (!isValid && document.length >= 11) {
					setError(field, { message: "CPF inválido, revise o documento" });
				} else {
					clearErrors(field);
				}
			},
			default: () => {},
		};

		(action[field] || action.default)();
	}

	const SupportForm = useMemo(
		() =>
			isDesktop && (
				<TMPopover
					labelButton="Ajuda"
					iconButton="MdHelpOutline"
					onChange={handleChange}
					forceClose={statePopover}
				>
					<TMPopoverHeader
						onClick={() => setStatePopover(!statePopover)}
						showCloseButton
					>
						Fale conosco
					</TMPopoverHeader>
					<TMPopoverBody>
						<FormSupport onSubmit={handleSubmit(submit)}>
							<Controller
								name="document"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="CPF"
										placeholder="Informe seu CPF"
										mask="999.999.999-99"
										errorMessage={
											formState
												.errors
												.document
										}
									/>
								)}
							/>
							<Input
								label="Nome completo"
								placeholder="Informe seu nome completo"
								fieldRegister="user"
								register={register}
							/>
							<Select
								label="Alterar dados"
								placeholder="Selecione o que deseja alterar"
								defaultValue={{
									value: "e-mail",
									label: "E-mail",
								}}
								onChange={(item) =>
									setValue(
										"alternative",
										item.label
									)
								}
								options={[
									{
										value: "e-mail",
										label: "E-mail",
									},
									{
										value: "phone",
										label: "Telefone",
									},
								]}
							/>

							{alternative === "Telefone" && (
								<Input
									label="E-mail"
									placeholder="Informe seu e-mail atual"
									fieldRegister="userContact"
									register={register}
								/>
							)}

							<Input
								label={`${selectedField} antigo`}
								placeholder={`Informe seu ${String(selectedField).toLowerCase()} antigo`}
								fieldRegister="oldData"
								register={register}
							/>
							<Input
								label={`${selectedField} novo`}
								placeholder={`Informe seu ${String(selectedField).toLowerCase()} novo`}
								fieldRegister="newData"
								register={register}
							/>
							<Controller
								name="dtBirthday"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Data de nascimento"
										placeholder="Informe sua data de nascimento"
										mask="99/99/9999"
									/>
								)}
							/>
							<Button
								title="Enviar"
								type="submit"
								loading={ticketLoading}
								disabled={
									isDataEmpty ||
									(formState.errors &&
										Object.keys(
											formState.errors
										).length)
								}
							/>
						</FormSupport>
					</TMPopoverBody>
				</TMPopover>
			),
		[alternative, selectedField, isDataEmpty, formState]
	);

	return <div style={{ display: config.enableSupport ? "block" : "none" }}>{SupportForm}</div>;
};

export default Support;
